






























import Component from 'vue-class-component'
import Vue from 'vue'
import {
  EUserPermissions,
  EUserStatus,
  IBatchTemplate,
  IFacility,
  IFacilityAdvancedSettings,
  IPayerType,
  getIdFromEntityField,
  isNumber
} from '@zunta/configurations'
import { FacilityModule } from '@/store/modules/facility.module'
import { getModule } from 'vuex-module-decorators'
import { TemplateModule } from '@/store/modules/template.module'
import { UserModule } from '@/store/modules/user.module'
import Card from '@/components/Card.vue'
import BatchFormV2 from '@/views/app/management/settings/pages/facility/batch/components/BatchFormV2.vue'
import LoaderWrapper from '@/components/layout/LoaderWrapper.vue'
import { User } from '@/classes/User'
import Api from '@/api'

const moment: any = require('moment')
@Component({
  components: { LoaderWrapper, BatchFormV2, Card }
})

export default class BatchSettings extends Vue {
  userModuleInstance: UserModule = getModule(UserModule, this.$store)
  facilityModuleInstance: FacilityModule = getModule(FacilityModule, this.$store)
  templateModuleInstance: TemplateModule = getModule(TemplateModule, this.$store)

  advancedSettingsItems: IFacilityAdvancedSettings[] = []
  batchTemplates: IBatchTemplate[] = []
  updater: number = 0

  isLoading: boolean = false
  user: User = Api.getUser() as User

  get batchTemplatesData () {
    return (batchId: number | undefined) => {
      if (!this.batchTemplates || !this.batchTemplates.length) {
        return []
      }
      return this.batchTemplates.filter((tpl: Partial<IBatchTemplate>) => tpl.settingsId === batchId)
    }
  }

  get selectedFacility (): IFacility {
    return this.$store.state.facility.selectedFacility
  }

  get payerTypes (): IPayerType[] {
    return this.$store.state.facility.payerTypes.filter((pt: any) => pt.active)
  }

  get facilityBatches (): IFacilityAdvancedSettings[] {
    const advancedSettings: IFacilityAdvancedSettings[] = this.$store.state.facility.facilityAdvancedSettings || []
    const advancedSettingsWithActivePayers = advancedSettings.map(setting => {
      return {
        ...setting,
        payers: this.filterPayers(setting.payers),
        secondaryPayers: this.filterPayers(setting.secondaryPayers),
        payersToExclude: this.filterPayers(setting.payersToExclude)
      }
    })
    return advancedSettingsWithActivePayers
  }

  filterPayers (payers: number[] | undefined) {
    if (!payers || !payers.length) return []
    return payers.filter(payer => this.payerTypes.map(payer => payer.id).includes(+payer))
  }

  get batchesWithNoNames () {
    return !!this.facilityBatches.find((batch: IFacilityAdvancedSettings) => !batch.name)
  }

  get hasEditPermission () {
    return this.user.hasPermissionFor(EUserPermissions.EDIT_FACILITY_ADVANCED_SETTINGS)
  }

  async mounted () {
    try {
      this.isLoading = true

      await Promise.all([
        this.facilityModuleInstance.fetchPayerTypes((this.selectedFacility as IFacility).id as number),
        this.templateModuleInstance.fetchTemplates({}),
        this.facilityModuleInstance.fetchFacilityAdvancedSettings(this.selectedFacility.id as number),
        this.userModuleInstance.getManagementUsersList({ status: EUserStatus.ACTIVE })
      ])

      this.advancedSettingsItems = JSON.parse(JSON.stringify(this.facilityBatches))
      if (!this.advancedSettingsItems.length) {
        this.initAdvancedEmptySettings(true)
      } else {
        await this.getBatchTemplates()
      }
    } finally {
      this.isLoading = false
    }
    window.addEventListener('message', async event => {
      if (event.data.type === 'refreshChainTemplates') {
        await this.templateModuleInstance.fetchTemplates({})
        this.$forceUpdate()
      }
    })
  }

  collapseAllBatchesExceptIndex (index: number) {
    this.advancedSettingsItems.forEach((batch: any, batchIndex: number) => {
      if (index !== batchIndex && this.$refs[`batch-${batchIndex}`]) {
        // @ts-ignore
        this.$refs[`batch-${batchIndex}`][0].$children[0].collapse()
      }
    })
    setTimeout(() => {
      const element = document.body.querySelector(`.facility-batch-setting:nth-child(${index + 1})`)
      if (!element) {
        return
      }

      const headerOffset = 89
      const elementPosition = element.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      })
    }, 300)
  }

  async getBatchTemplates (batchId?: number) {
    const req: any[] = isNumber(batchId)
      ? [this.getBatchSettingsTemplates(batchId)]
      : this.advancedSettingsItems
        .filter((batch: IFacilityAdvancedSettings) => batch.id)
        .map((batch: IFacilityAdvancedSettings) => this.getBatchSettingsTemplates(batch.id as number))
    await Promise.all(req)
  }

  async getBatchSettingsTemplates (batchId: number) {
    if (!batchId) {
      return []
    }

    const settingsTemplates = await this.templateModuleInstance.getBatchSettingsTemplates(batchId)
    if (!settingsTemplates) {
      return []
    }

    const filteredTemplates: IBatchTemplate[] = this.batchTemplates && this.batchTemplates.length
      ? this.batchTemplates.filter((item: IBatchTemplate) => item.settingsId !== batchId)
      : []

    settingsTemplates.forEach((item: IBatchTemplate) => {
      item.template = getIdFromEntityField(item.template)
      item.payers = item.payers ? (item.payers as any[]).map(Number) : null
    })
    this.batchTemplates = settingsTemplates.concat(filteredTemplates)
  }

  initAdvancedEmptySettings (isDefault: boolean = false) {
    this.advancedSettingsItems.push({
      facility: this.selectedFacility.id as number,
      monthsBack: undefined,
      payers: (this.payerTypes || []).map((pt: IPayerType) => pt.id) as number[],
      billingDay: undefined,
      billingContact: undefined,
      reviewUser: undefined,
      reviewStatementsBeforeMailing: true,
      name: isDefault ? 'Default Batch' : '',
      dueDate: undefined,
      zeroBalanceAction: undefined,
      negativeBalanceAction: undefined,
      controlBalance: undefined,
      controlBalanceAction: undefined,
      ncsCollectionAction: undefined,
      ncsDirectDepositAction: undefined,
      pccBillingCycleManager: undefined,
      printedStatementUser: undefined,
      secondaryPayers: [],
      payersSettings: null,
      secondaryPayersSettings: null,
      ncsDirectDepositOption: undefined,
      includeAllPayers: true
    })
  }

  async deleteBatchSettingsTemplate (batchTemplate: Partial<IBatchTemplate>) {
    if (batchTemplate.id) {
      try {
        this.isLoading = true
        await this.templateModuleInstance.deleteBatchSettingsTemplates(batchTemplate.id)
        await this.getBatchTemplates(batchTemplate.settingsId)
      } finally {
        this.isLoading = false
      }
    }
  }

  async deleteBatch (index: number) {
    try {
      this.isLoading = true

      if (this.advancedSettingsItems[+index].id) {
        await this.deleteBatchSettingsTemplate(this.advancedSettingsItems[+index])
        await this.facilityModuleInstance.deleteFacilityAdvancedSetting({
          facilityId: this.selectedFacility.id,
          settingId: this.advancedSettingsItems[+index].id
        })
      }
      this.advancedSettingsItems.splice(+index, 1)

      if (!this.advancedSettingsItems.length) {
        this.initAdvancedEmptySettings(true)
      }
      this.updater++
    } finally {
      this.isLoading = false
    }
  }

  async updateSettings (data: { batch: IFacilityAdvancedSettings, templates?: IBatchTemplate[] }, index: number, save: boolean = false) {
    try {
      this.isLoading = true

      this.advancedSettingsItems[index] = {
        ...data.batch,
        facility: this.selectedFacility.id as number
      }

      if (!save) {
        return
      }

      await this.saveAdvancedSettings()

      if (data.templates && data.templates.length) {
        const promises: any[] = data.templates.map((template: IBatchTemplate) =>
          this.templateModuleInstance.updateBatchSettingTemplate({
            ...template,
            settingsId: template.settingsId ? template.settingsId : this.advancedSettingsItems[index].id
          }))

        await Promise.all(promises)
        await this.getBatchTemplates(data.batch.id)
      }
    } finally {
      this.isLoading = false
    }
  }

  async saveAdvancedSettings () {
    await this.facilityModuleInstance.saveFacilityAdvancedSettings({
      facilityId: this.selectedFacility.id,
      settings: this.advancedSettingsItems
    })
    await this.facilityModuleInstance.fetchFacilityAdvancedSettings(this.selectedFacility.id as number)
    this.advancedSettingsItems = JSON.parse(JSON.stringify(this.facilityBatches))
  }
}
